import { useCallback, useEffect } from 'react';
import { useCancellationSurvey } from './useCancellationSurvey';
import { QUESTION_TYPES, SPECIAL_ANSWERS } from '../constants/cancellation';

interface UseCancellationSurveyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete?: () => void;
}

export const useCancellationSurveyModal = ({ isOpen, onClose, onComplete }: UseCancellationSurveyModalProps) => {
    const {
        currentQuestion,
        currentQuestionId,
        answers,
        canSkip,
        isCurrentAnswerValid,
        isFirstQuestion,
        isSurveyComplete,
        handleAnswer,
        goToNextQuestion,
        goToPreviousQuestion,
        submitSurvey,
        trackStart,
        trackSkipped,
        resetSurvey,
    } = useCancellationSurvey();

    const currentAnswer = answers[currentQuestionId];

    // Track survey start when modal opens
    useEffect(() => {
        if (isOpen) {
            trackStart();
        }
    }, [isOpen, trackStart]);

    // Reset survey when modal closes
    useEffect(() => {
        if (!isOpen) {
            resetSurvey();
        }
    }, [isOpen, resetSurvey]);

    // Determine if we should show the Next button
    // Show for multiSelect, text types, OR select type when "other" is selected (needs text input)
    const isOtherSelected =
        currentQuestion?.type === QUESTION_TYPES.SELECT &&
        (currentAnswer === SPECIAL_ANSWERS.OTHER ||
            (typeof currentAnswer === 'string' && currentAnswer.startsWith(`${SPECIAL_ANSWERS.OTHER}: `)));
    const showNextButton =
        currentQuestion?.type === QUESTION_TYPES.MULTI_SELECT ||
        currentQuestion?.type === QUESTION_TYPES.TEXT ||
        isOtherSelected;

    // Handle modal close
    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    // Handle skip - just advance to next step, don't cancel subscription
    const handleSkip = useCallback(() => {
        trackSkipped();
        // Always use onComplete to advance to next step in flow
        // The flow orchestrator will handle what happens next
        if (onComplete) {
            onComplete();
        } else {
            // Fallback if used outside of orchestrator
            handleClose();
        }
    }, [trackSkipped, handleClose, onComplete]);

    // Handle back navigation
    const handleBack = useCallback(() => {
        goToPreviousQuestion();
    }, [goToPreviousQuestion]);

    // Handle next/submit
    const handleNext = useCallback(async () => {
        const hasNextQuestion = goToNextQuestion();

        if (!hasNextQuestion || isSurveyComplete) {
            // Survey is complete
            await submitSurvey();
            if (onComplete) {
                onComplete();
            } else {
                handleClose();
            }
            return;
        }
    }, [goToNextQuestion, isSurveyComplete, submitSurvey, handleClose, onComplete]);

    // Handle answer with auto-navigation logic
    const handleAnswerWithNavigation = useCallback(
        (answer: string | string[]) => {
            handleAnswer(answer);

            const isSelectType = currentQuestion?.type === QUESTION_TYPES.SELECT;
            const isOther =
                answer === SPECIAL_ANSWERS.OTHER ||
                (typeof answer === 'string' && answer.startsWith(`${SPECIAL_ANSWERS.OTHER}: `));

            // Auto-navigate for select type (except "Other")
            if (isSelectType && !isOther) {
                // Pass the answer directly so navigation uses it
                goToNextQuestion(answer);
            }
        },
        [handleAnswer, currentQuestion?.type, goToNextQuestion]
    );

    return {
        // Survey state
        currentQuestion,
        currentAnswer,
        canSkip,
        isCurrentAnswerValid,
        isFirstQuestion,
        isSurveyComplete,
        showNextButton,

        // Handlers
        handleClose,
        handleSkip,
        handleBack,
        handleNext,
        handleAnswerWithNavigation,
    };
};
